import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getLiveWallSnapshot } from '../lib/liveWallApi';
import { getLocale } from '../i18n/format';
import './LiveWallPage.css';

const defaultWidgets = {
  kpi: {
    activeListeners: true,
    peakToday: true,
    reach: true,
    adr: true,
    aha: true,
    aqh: true,
    tsl: true,
    newListeners: true,
  },
  main: {
    audienceToday: true,
    geoMap: true,
    liveActivity: true,
    devices: true,
  },
  detail: {
    activeListenersFeed: true,
  },
};

function formatDashboardDate(date, locale) {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function formatDashboardTime(date) {
  return [
    String(date.getHours()).padStart(2, '0'),
    String(date.getMinutes()).padStart(2, '0'),
    String(date.getSeconds()).padStart(2, '0'),
  ].join(':');
}

function formatLiveNumber(value, locale = 'en-US') {
  const numericValue = Number.parseInt(String(value).replace(/[^\d-]/g, ''), 10);
  return Number.isFinite(numericValue) ? new Intl.NumberFormat(locale).format(numericValue) : '0';
}

function getKpiValue(kpiCards, widgetKey) {
  return kpiCards.find((kpi) => kpi.widgetKey === widgetKey)?.value ?? '0';
}

function buildKpiCards(kpi = {}, t, locale) {
  return [
    {
      label: t('live_wall.public.kpi.active_listeners'),
      value: formatLiveNumber(kpi.activeListeners ?? 0, locale),
      note: t('live_wall.public.kpi.live_now'),
      accent: 'live',
      widgetKey: 'activeListeners',
    },
    {
      label: t('live_wall.public.kpi.peak_today'),
      value: formatLiveNumber(kpi.peakToday ?? 0, locale),
      widgetKey: 'peakToday',
    },
    {
      label: t('live_wall.public.kpi.reach'),
      value: formatLiveNumber(kpi.reach ?? 0, locale),
      widgetKey: 'reach',
    },
    {
      label: 'ADR',
      value: formatLiveNumber(kpi.adr ?? 0, locale),
      widgetKey: 'adr',
    },
    {
      label: 'AHA',
      value: formatLiveNumber(kpi.aha ?? 0, locale),
      widgetKey: 'aha',
    },
    {
      label: 'AQH',
      value: formatLiveNumber(kpi.aqh ?? 0, locale),
      widgetKey: 'aqh',
    },
    {
      label: 'TSL',
      value: kpi.tsl ?? '0m',
      widgetKey: 'tsl',
    },
    {
      label: t('live_wall.public.kpi.new_listeners'),
      value: formatLiveNumber(kpi.newListeners ?? 0, locale),
      widgetKey: 'newListeners',
    },
  ];
}

function mergeWidgets(widgets) {
  return {
    kpi: { ...defaultWidgets.kpi, ...(widgets?.kpi || {}) },
    main: { ...defaultWidgets.main, ...(widgets?.main || {}) },
    detail: { ...defaultWidgets.detail, ...(widgets?.detail || {}) },
  };
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function getPointValue(point) {
  const raw = point?.value ?? point?.listeners ?? point?.count ?? point?.peak ?? null;
  const numericValue = Number.parseFloat(String(raw).replace(/[^\d.-]/g, ''));
  return Number.isFinite(numericValue) ? numericValue : null;
}

function buildPeakLoadChart(points, currentListenersValue = null) {
  const rawSourcePoints = asArray(points);
  const parsedCurrentValue = Number.parseFloat(String(currentListenersValue ?? '').replace(/[^\d.-]/g, ''));
  const hasCurrentValue = Number.isFinite(parsedCurrentValue);
  const sourcePoints = hasCurrentValue && parsedCurrentValue > 0
    ? rawSourcePoints.slice(0, Math.max(1, rawSourcePoints.findLastIndex((point) => (getPointValue(point) || 0) > 0) + 1))
    : rawSourcePoints;
  const values = sourcePoints.map(getPointValue);
  const hasValues = values.some((value) => value != null);
  const chart = {
    left: 34,
    right: 812,
    top: 76,
    bottom: 286,
    baseline: 318,
  };

  const chartPoints = sourcePoints
    .map((point, index) => {
      const label = point?.label || point?.time || point?.hour || String(index + 1);

      if (hasValues) {
        const value = values[index] ?? 0;
        const validValues = values.filter((item) => item != null);
        const minValue = Math.min(...validValues, 0);
        const maxValue = Math.max(...validValues, 1);
        const range = maxValue - minValue || 1;
        const x =
          sourcePoints.length === 1
            ? (chart.left + chart.right) / 2
            : chart.left + ((chart.right - chart.left) * index) / (sourcePoints.length - 1);
        const y = chart.bottom - ((value - minValue) / range) * (chart.bottom - chart.top);

        return { label, value, x, y };
      }

      if (Number.isFinite(point?.x) && Number.isFinite(point?.y)) {
        return { label, value: null, x: point.x, y: point.y };
      }

      return null;
    })
    .filter(Boolean);

  const linePath = chartPoints
    .map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x} ${point.y}`)
    .join(' ');
  const areaPath =
    chartPoints.length > 0
      ? `${linePath} L${chartPoints[chartPoints.length - 1].x} ${chart.baseline} L${chartPoints[0].x} ${chart.baseline} Z`
      : '';
  const peakPoint = chartPoints.reduce((peak, point) => {
    if (!peak) return point;
    if (point.value == null || peak.value == null) return point.y < peak.y ? point : peak;
    return point.value > peak.value ? point : peak;
  }, null);
  const currentPoint = chartPoints[chartPoints.length - 1] || null;
  if (currentPoint && hasCurrentValue) {
    currentPoint.value = parsedCurrentValue;
    const validValues = chartPoints.map((point) => point.value).filter((item) => item != null);
    const minValue = Math.min(...validValues, 0);
    const maxValue = Math.max(...validValues, parsedCurrentValue, 1);
    const range = maxValue - minValue || 1;
    currentPoint.y = chart.bottom - ((parsedCurrentValue - minValue) / range) * (chart.bottom - chart.top);
  }

  return { areaPath, linePath, chartPoints, peakPoint, currentPoint };
}

function shortenGeoLabel(label, fallbackLabel = 'Listener') {
  const text = String(label || fallbackLabel);
  return text.length > 14 ? `${text.slice(0, 13)}...` : text;
}

const GEO_CENTER = { x: 260, y: 105 };

const GEO_LOCATION_SLOTS = [
  { x: 130, y: 30, labelDx: 10, labelDy: -6 },
  { x: 260, y: 20, labelDx: 10, labelDy: -6 },
  { x: 390, y: 30, labelDx: 10, labelDy: -6 },
  { x: 95, y: 105, labelDx: 10, labelDy: -6 },
  { x: 425, y: 105, labelDx: 10, labelDy: -6 },
  { x: 130, y: 180, labelDx: 10, labelDy: 14 },
  { x: 260, y: 190, labelDx: 10, labelDy: 14 },
  { x: 390, y: 180, labelDx: 10, labelDy: 14 },
  { x: 185, y: 70, labelDx: 10, labelDy: -6 },
  { x: 335, y: 70, labelDx: 10, labelDy: -6 },
  { x: 185, y: 140, labelDx: 10, labelDy: 14 },
  { x: 335, y: 140, labelDx: 10, labelDy: 14 },
];

const GEO_LAYOUT_ORDER_BY_COUNT = {
  1: [4],
  2: [3, 4],
  3: [3, 1, 4],
};

const GEO_LAYOUT_ORDER = [
  0,
  2,
  5,
  7,
  1,
  6,
  4,
  3,
  8,
  9,
  10,
  11,
];

function buildGeoNodes(items, fallbackLabel) {
  const groupedItems = asArray(items).reduce((acc, item, index) => {
    const key = item?.city || item?.country || item?.label || `location-${index}`;
    const value = Number.parseFloat(item?.value ?? 1) || 1;
    const current = acc.get(key);

    acc.set(key, {
      key,
      value: (current?.value || 0) + value,
    });

    return acc;
  }, new Map());

  const nodes = Array.from(groupedItems.values())
    .sort((a, b) => {
      if (b.value !== a.value) return b.value - a.value;
      return a.key.localeCompare(b.key);
    })
    .slice(0, 8);
  const layoutOrder = GEO_LAYOUT_ORDER_BY_COUNT[nodes.length] || GEO_LAYOUT_ORDER;

  const positionedNodes = nodes.map((item, index) => {
      const layoutIndex = layoutOrder[index % layoutOrder.length];
      const slot = GEO_LOCATION_SLOTS[layoutIndex];
      const value = item.value;
      const radius = 5 + Math.min(value, 10) * 0.7;

      return {
        key: `${item.key}-${index}`,
        label: shortenGeoLabel(item.key, fallbackLabel),
        labelDx: slot.labelDx,
        labelDy: slot.labelDy,
        radius,
        x: slot.x,
        y: slot.y,
      };
    });

  return { center: GEO_CENTER, nodes: positionedNodes };
}

function LiveActivityCard({ liveActivity, t }) {
  const points = asArray(liveActivity?.points);

  return (
    <section className="glass-card activity-card" aria-label={t('live_wall.public.aria.live_activity')}>
      <div className="section-heading compact-heading">
        <div>
          <p className="eyebrow">{t('live_wall.public.activity.last_15_minutes')}</p>
          <h2>{t('live_wall.public.activity.title')}</h2>
        </div>
      </div>

      <div className="activity-bars" aria-label={t('live_wall.public.aria.activity_deltas')}>
        {points.map((point, index) => {
          const value = Number(point?.delta || 0);

          return (
            <div className="activity-bar-slot" key={`${point?.label || index}-${value}`}>
              <span
                className={`activity-bar ${value < 0 ? 'is-negative' : ''}`}
                style={{ height: `${Math.min(Math.max(Math.abs(value) * 22 + 14, 36), 106)}px` }}
              />
              <strong>{value > 0 ? `+${value}` : value}</strong>
            </div>
          );
        })}
      </div>

      <div className="activity-stats">
        <div>
          <p>{t('live_wall.public.activity.new_joins')}</p>
          <strong>{liveActivity?.newJoins ?? 0}</strong>
        </div>
        <div>
          <p>{t('live_wall.public.activity.disconnects')}</p>
          <strong>{liveActivity?.disconnects ?? 0}</strong>
        </div>
        <div className="is-net">
          <p>{t('live_wall.public.activity.net_change')}</p>
          <strong>{liveActivity?.netChange ?? 0}</strong>
        </div>
      </div>
    </section>
  );
}

function DevicesCard({ devices, t }) {
  const segments = asArray(devices?.segments);
  const dominantValue = devices?.dominant?.value;
  const dominantLabel = devices?.dominant?.label;

  return (
    <section className="glass-card devices-card" aria-label={t('live_wall.public.aria.devices')}>
      <div>
        <p className="eyebrow">{t('live_wall.public.devices.current_audience')}</p>
        <h2>{t('live_wall.public.devices.title')}</h2>
      </div>

      <div className="devices-layout">
        <div className="donut-chart" aria-label={t('live_wall.public.aria.device_split')}>
          <div className="donut-hole">
            <strong>{dominantValue != null ? `${dominantValue}%` : '0%'}</strong>
            <span>{dominantLabel || t('live_wall.public.devices.fallback')}</span>
          </div>
        </div>

        <div className="devices-legend">
          {segments.map((device) => (
            <div className="device-legend-row" key={device.label}>
              <span>{device.label}</span>
              <strong>{device.value}%</strong>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DisabledWall({ title, message, label, t }) {
  return (
    <main className="live-wall-page disabled-wall" aria-label={label}>
      <section>
        <p className="eyebrow">{t('live_wall.public.brand')}</p>
        <h1>{title}</h1>
        {message ? <p>{message}</p> : null}
      </section>
    </main>
  );
}

export default function LiveWallPage() {
  const { t } = useTranslation('common');
  const { token } = useParams();
  const audioRef = useRef(null);
  const [snapshot, setSnapshot] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasDataError, setHasDataError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [topStatus, setTopStatus] = useState({
    now: new Date(),
    secondsSinceUpdate: 0,
  });

  useEffect(() => {
    document.title = t('live_wall.public.document_title');
  }, [t]);

  useEffect(() => {
    let isMounted = true;
    let isRequestActive = false;

    const fetchSnapshot = async ({ initial = false } = {}) => {
      if (isRequestActive) return;

      isRequestActive = true;

      if (initial) {
        setIsLoading(true);
        setHasDataError(false);
      }

      try {
        const nextSnapshot = await getLiveWallSnapshot(token);

        if (!isMounted) return;

        setSnapshot(nextSnapshot);
        setHasDataError(false);
        setTopStatus({
          now: new Date(),
          secondsSinceUpdate: 0,
        });
      } catch {
        if (isMounted && initial) {
          setHasDataError(true);
        }
      } finally {
        isRequestActive = false;

        if (isMounted && initial) {
          setIsLoading(false);
        }
      }
    };

    fetchSnapshot({ initial: true });

    const intervalId = window.setInterval(() => {
      fetchSnapshot();
    }, 5000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [token]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setTopStatus((current) => ({
        now: new Date(),
        secondsSinceUpdate: current.secondsSinceUpdate + 1,
      }));
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, []);

  const settings = snapshot?.settings || {};
  const streamPlaybackUrl = settings.streamPlaybackUrl || '';
  const data = snapshot?.data || {};
  const nowPlayingTitle = String(data.nowPlaying?.title || '').trim();
  const locale = getLocale();
  const widgets = useMemo(() => mergeWidgets(settings.widgets), [settings.widgets]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    audio.removeAttribute('src');
    audio.load();
    setIsPlaying(false);
  }, [streamPlaybackUrl]);

  async function handlePlaybackToggle() {
    const audio = audioRef.current;

    if (!audio || !settings.playbackEnabled || !streamPlaybackUrl) {
      setIsPlaying(false);
      return;
    }

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    try {
      audio.src = streamPlaybackUrl;
      audio.load();
      await audio.play();
      setIsPlaying(true);
    } catch (error) {
      console.error('[LiveWall] playback failed', error);
      setIsPlaying(false);
    }
  }

  if (isLoading) {
    return (
      <DisabledWall
        title={t('live_wall.public.loading.title')}
        label={t('live_wall.public.loading.label')}
        t={t}
      />
    );
  }

  if (hasDataError) {
    return (
      <DisabledWall
        title={t('live_wall.public.error.title')}
        label={t('live_wall.public.error.label')}
        t={t}
      />
    );
  }

  if (settings.enabled === false) {
    return (
      <DisabledWall
        title={t('live_wall.public.disabled.title')}
        message={t('live_wall.public.disabled.message')}
        label={t('live_wall.public.disabled.label')}
        t={t}
      />
    );
  }

  const station = settings.station || {};
  const kpi = buildKpiCards(data.kpi || {}, t, locale);
  const audienceToday = asArray(data.audienceToday);
  const activeListenersValue = getKpiValue(kpi, 'activeListeners');
  const peakLoadChart = buildPeakLoadChart(audienceToday, activeListenersValue);
  const geoConnections = asArray(data.geoConnections);
  const geoField = buildGeoNodes(geoConnections, t('live_wall.public.geo.listener_fallback'));
  const topCountries = asArray(data.topCountries);
  const activeListenersFeed = asArray(data.activeListenersFeed);
  const liveActivity = data.liveActivity || {};
  const devices = data.devices || {};
  const dashboardDate = formatDashboardDate(topStatus.now, locale);
  const dashboardTime = formatDashboardTime(topStatus.now);
  const peakTodayValue = getKpiValue(kpi, 'peakToday');
  const hasFeed = widgets.detail.activeListenersFeed;
  const hasActivity = widgets.main.liveActivity;
  const hasDevices = widgets.main.devices;
  const showDevicesInMain = hasFeed && !hasActivity && hasDevices;
  const showDevicesInBottom = hasFeed && hasActivity && hasDevices;
  const hasMainThirdColumn = (!hasFeed && (hasActivity || hasDevices)) || showDevicesInMain || hasActivity;
  const mainGridItemCount = [
    widgets.main.audienceToday,
    widgets.main.geoMap,
    hasMainThirdColumn,
  ].filter(Boolean).length;
  const hasMainRow = mainGridItemCount > 0;
  const visibleKpiCards = kpi.filter((kpiCard) => {
    return kpiCard.widgetKey ? widgets.kpi[kpiCard.widgetKey] : true;
  });
  const wallClasses = [
    'live-wall-page',
    'wall-shell',
    hasFeed ? 'has-feed' : 'no-feed',
    hasActivity ? 'has-activity' : 'no-activity',
    hasDevices ? 'has-devices' : 'no-devices',
    hasMainRow ? 'has-main-row' : 'no-main-row',
    hasMainRow ? `main-items-${mainGridItemCount}` : '',
    hasActivity && hasDevices ? 'has-activity-and-devices' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const kpiGridTemplateColumns =
    visibleKpiCards.length > 0 && visibleKpiCards.length !== kpi.length
      ? `repeat(${visibleKpiCards.length}, minmax(0, 1fr))`
      : undefined;

  return (
    <main className={wallClasses} aria-label={t('live_wall.public.aria.dashboard')}>
      <header className="top-bar">
        <div className="brand-cluster">
          <div className="brand-mark" aria-hidden="true">
            <img src="/Logo500-2.png" alt="" />
          </div>
          <div className="brand-copy">
            <p>RADIOMONITOR</p>
            <strong>LIVE WALL</strong>
          </div>
          <div className="station-logo" aria-label={t('live_wall.public.aria.station_logo', { station: station.name || t('live_wall.public.station_fallback') })}>
            {settings.stationLogoSrc ? (
              <img src={settings.stationLogoSrc} alt={t('live_wall.public.aria.station_logo', { station: station.name || t('live_wall.public.station_fallback') })} />
            ) : (
              <span>{station.logoText || 'RM'}</span>
            )}
          </div>
          <div className="station-block">
            <span className="station-label">{t('live_wall.public.station_label')}</span>
            <h1>{station.name || t('live_wall.public.station_fallback')}</h1>
          </div>
          <span className="live-pill">
            <span className="live-dot" />
            {station.status || t('live_wall.public.live_status')}
          </span>
        </div>

        <div className="center-broadcast">
          <button
            className={`playback-button ${isPlaying ? 'is-playing' : ''}`}
            type="button"
            aria-pressed={isPlaying}
            disabled={!settings.playbackEnabled || !streamPlaybackUrl}
            onClick={handlePlaybackToggle}
          >
            <span className="playback-icon" aria-hidden="true">
              {isPlaying ? 'II' : t('live_wall.public.playback.icon_play')}
            </span>
            <span>
              <small>{t('live_wall.public.playback.label')}</small>
              <strong>{isPlaying ? t('live_wall.public.playback.pause') : t('live_wall.public.playback.play')}</strong>
            </span>
          </button>
          {nowPlayingTitle ? (
            <div className="now-playing" title={nowPlayingTitle}>
              <small>{t('live_wall.public.now_playing.label')}</small>
              <strong>{nowPlayingTitle}</strong>
            </div>
          ) : null}
          <audio
            ref={audioRef}
            preload="none"
            onEnded={() => setIsPlaying(false)}
            onPause={() => setIsPlaying(false)}
            onPlay={() => setIsPlaying(true)}
          />
        </div>

        <div className="top-meta" aria-label={t('live_wall.public.aria.live_status')}>
          <span>{dashboardDate}</span>
          <strong>{dashboardTime}</strong>
          <span className={`status-chip ${isPlaying ? 'is-streaming' : ''}`}>{station.streamStatus || t('live_wall.public.online')}</span>
        </div>
      </header>

      <section className="kpi-strip" style={{ gridTemplateColumns: kpiGridTemplateColumns }} aria-label={t('live_wall.public.aria.kpi')}>
        {visibleKpiCards.map((kpiCard) => (
          <article className={`kpi-card ${kpiCard.accent === 'live' ? 'is-primary' : ''}`} key={kpiCard.label}>
            <p>{kpiCard.label}</p>
            <strong>{kpiCard.value}</strong>
            {kpiCard.note ? <span className="kpi-note">{kpiCard.note}</span> : null}
          </article>
        ))}
      </section>

      {hasMainRow ? (
        <section className="main-grid" aria-label={t('live_wall.public.aria.main_grid')}>
          {widgets.main.audienceToday ? (
          <section className="glass-card chart-card" aria-label={t('live_wall.public.aria.peak_chart')}>
            <div className="section-heading">
              <div>
                <p className="eyebrow">{t('live_wall.public.chart.eyebrow')}</p>
                <h2>{t('live_wall.public.chart.title')}</h2>
              </div>
              <span className="signal-chip">{t('live_wall.public.chart.today')}</span>
            </div>

            <div className="chart-frame">
              <svg className="audience-chart" viewBox="0 0 860 360" role="img" aria-labelledby="audienceChartTitle audienceChartDesc">
                <title id="audienceChartTitle">{t('live_wall.public.chart.title')}</title>
                <desc id="audienceChartDesc">{t('live_wall.public.chart.description')}</desc>
                <defs>
                  <linearGradient id="areaGradient" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#42d9ff" stopOpacity="0.45" />
                    <stop offset="46%" stopColor="#6f7bff" stopOpacity="0.18" />
                    <stop offset="100%" stopColor="#080d1b" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id="lineGradient" x1="0" x2="1" y1="0" y2="0">
                    <stop offset="0%" stopColor="#35dcff" />
                    <stop offset="54%" stopColor="#6287ff" />
                    <stop offset="100%" stopColor="#b987ff" />
                  </linearGradient>
                  <radialGradient id="markerGradient">
                    <stop offset="0%" stopColor="#ffffff" />
                    <stop offset="42%" stopColor="#42d9ff" />
                    <stop offset="100%" stopColor="#42d9ff" stopOpacity="0" />
                  </radialGradient>
                  <filter id="lineGlow" x="-10%" y="-70%" width="120%" height="240%">
                    <feGaussianBlur stdDeviation="7" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                <g className="chart-grid-lines">
                  <line x1="34" x2="812" y1="70" y2="70" />
                  <line x1="34" x2="812" y1="138" y2="138" />
                  <line x1="34" x2="812" y1="206" y2="206" />
                  <line x1="34" x2="812" y1="274" y2="274" />
                </g>
                <g className="chart-grid-columns">
                  <line x1="196" x2="196" y1="42" y2="314" />
                  <line x1="372" x2="372" y1="42" y2="314" />
                  <line x1="584" x2="584" y1="42" y2="314" />
                  <line x1="812" x2="812" y1="42" y2="314" />
                </g>

                {peakLoadChart.areaPath ? <path className="area-path" d={peakLoadChart.areaPath} /> : null}
                {peakLoadChart.linePath ? <path className="glow-path" d={peakLoadChart.linePath} /> : null}
                {peakLoadChart.linePath ? <path className="line-path" d={peakLoadChart.linePath} /> : null}

                {peakLoadChart.chartPoints.map((point, index) => (
                  <g className="chart-point" key={`${point.label}-${index}`}>
                    <circle cx={point.x} cy={point.y} r="4.5" />
                    <circle cx={point.x} cy={point.y} r="12" />
                  </g>
                ))}

                {peakLoadChart.peakPoint ? (
                  <g className="peak-marker">
                    <line x1={peakLoadChart.peakPoint.x} x2={peakLoadChart.peakPoint.x} y1={peakLoadChart.peakPoint.y} y2="318" />
                    <circle cx={peakLoadChart.peakPoint.x} cy={peakLoadChart.peakPoint.y} r="18" />
                    <circle cx={peakLoadChart.peakPoint.x} cy={peakLoadChart.peakPoint.y} r="5" />
                    <text x={Math.max(42, Math.min(682, peakLoadChart.peakPoint.x - 66))} y={Math.max(34, peakLoadChart.peakPoint.y - 18)}>
                      {t('live_wall.public.chart.peak_marker', { value: formatLiveNumber(peakLoadChart.peakPoint.value ?? peakTodayValue, locale) })}
                    </text>
                  </g>
                ) : null}
                {peakLoadChart.currentPoint ? (
                  <g className="current-marker">
                    <line x1={peakLoadChart.currentPoint.x} x2={peakLoadChart.currentPoint.x} y1={peakLoadChart.currentPoint.y} y2="318" />
                    <circle cx={peakLoadChart.currentPoint.x} cy={peakLoadChart.currentPoint.y} r="22" />
                    <circle cx={peakLoadChart.currentPoint.x} cy={peakLoadChart.currentPoint.y} r="6" />
                    <text x={Math.max(42, Math.min(724, peakLoadChart.currentPoint.x - 88))} y={Math.min(332, peakLoadChart.currentPoint.y + 30)}>
                      {t('live_wall.public.chart.current_marker', { value: formatLiveNumber(peakLoadChart.currentPoint.value ?? activeListenersValue, locale) })}
                    </text>
                  </g>
                ) : null}
              </svg>

              <div className="time-axis" aria-hidden="true">
                {peakLoadChart.chartPoints.map((point, index) => (
                  <span key={`${point.label}-${index}`}>{point.label}</span>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        {widgets.main.geoMap ? (
          <section className="glass-card geo-card" aria-label={t('live_wall.public.aria.geo_map')}>
            <div className="section-heading">
              <div>
                <p className="eyebrow">{t('live_wall.public.geo.eyebrow')}</p>
                <h2>{t('live_wall.public.geo.title')}</h2>
              </div>
              <span className="signal-chip">{t('live_wall.public.geo.online_count', { value: formatLiveNumber(activeListenersValue, locale) })}</span>
            </div>

            <div className="geo-layout">
              <svg className="geo-map" viewBox="0 0 520 260" role="img" aria-label={t('live_wall.public.aria.geo_map')}>
                <defs>
                  <radialGradient id="geoPulse">
                    <stop offset="0%" stopColor="#f1fdff" />
                    <stop offset="42%" stopColor="#42d9ff" stopOpacity="0.66" />
                    <stop offset="100%" stopColor="#42d9ff" stopOpacity="0" />
                  </radialGradient>
                  <radialGradient id="geoCenterPulse">
                    <stop offset="0%" stopColor="#ffffff" />
                    <stop offset="38%" stopColor="#42d9ff" stopOpacity="0.72" />
                    <stop offset="100%" stopColor="#42d9ff" stopOpacity="0" />
                  </radialGradient>
                  <linearGradient id="mapStroke" x1="0" x2="1" y1="0" y2="1">
                    <stop offset="0%" stopColor="#42d9ff" stopOpacity="0.52" />
                    <stop offset="100%" stopColor="#a78bfa" stopOpacity="0.42" />
                  </linearGradient>
                </defs>
                <path className="map-land" d="M66 134 C104 62 192 48 248 74 C292 95 332 52 408 78 C472 100 496 160 454 203 C412 246 330 226 278 239 C198 260 118 232 128 188 C138 146 28 184 66 134 Z" />
                <path className="map-land secondary" d="M48 206 C86 186 130 194 164 218 C126 240 82 236 48 206 Z" />
                <path className="map-ring" d="M72 190 C154 128 262 92 390 118 C438 128 466 154 484 188" />
                <path className="map-ring" d="M56 78 C160 108 274 134 486 80" />
                <path className="map-ring" d="M94 224 C190 174 312 142 474 214" />

                {geoField.nodes.map((node, index) => (
                  <line
                    className="geo-link"
                    key={`line-${node.key}`}
                    x1={geoField.center.x}
                    y1={geoField.center.y}
                    x2={node.x}
                    y2={node.y}
                    style={{ animationDelay: `${index * 0.12}s` }}
                  />
                ))}

                <g className="geo-center-node">
                  <circle cx={geoField.center.x} cy={geoField.center.y} r="34" />
                  <circle cx={geoField.center.x} cy={geoField.center.y} r="10" />
                  <circle cx={geoField.center.x} cy={geoField.center.y} r="4" />
                </g>

                {geoField.nodes.map((node, index) => (
                  <g className="geo-point" style={{ animationDelay: `${index * 0.18}s` }} key={node.key}>
                    <circle cx={node.x} cy={node.y} r={node.radius * 2.8} />
                    <circle cx={node.x} cy={node.y} r={node.radius} />
                    <text x={node.x + node.labelDx} y={node.y + node.labelDy}>
                      {node.label}
                    </text>
                  </g>
                ))}
              </svg>

              <aside className="country-panel" aria-label={t('live_wall.public.aria.top_countries')}>
                <h3>{t('live_wall.public.geo.top_countries')}</h3>
                {topCountries.map((country) => {
                  const countryLabel = country.label || country.country || t('live_wall.public.geo.unknown');

                  return (
                    <div className="country-row" key={countryLabel}>
                      <div>
                        <span>{countryLabel}</span>
                        <strong>{country.value}%</strong>
                      </div>
                      <div className="bar-track">
                        <span style={{ width: `${country.value}%` }} />
                      </div>
                    </div>
                  );
                })}
              </aside>
            </div>
          </section>
        ) : null}

        {!hasFeed && (hasActivity || hasDevices) ? (
          <aside className="right-stack" aria-label={t('live_wall.public.aria.activity_and_devices')}>
            {hasActivity ? <LiveActivityCard liveActivity={liveActivity} t={t} /> : null}
            {hasDevices ? <DevicesCard devices={devices} t={t} /> : null}
          </aside>
        ) : showDevicesInMain ? (
          <DevicesCard devices={devices} t={t} />
        ) : hasActivity ? (
          <LiveActivityCard liveActivity={liveActivity} t={t} />
        ) : null}
        </section>
      ) : null}

      {hasFeed ? (
        <section className="bottom-grid" aria-label={t('live_wall.public.aria.feed_and_devices')}>
          <section className="glass-card feed-card" aria-label={t('live_wall.public.aria.active_feed')}>
            <div className="section-heading feed-heading">
              <div>
                <p className="eyebrow">{t('live_wall.public.feed.eyebrow')}</p>
                <h2>{t('live_wall.public.feed.title')}</h2>
              </div>
              <span className="signal-chip">{t('live_wall.public.feed.masked_ip_only')}</span>
            </div>

            <div className="feed-table" role="table" aria-label={t('live_wall.public.aria.feed_table')}>
              <div className="feed-row feed-head" role="row">
                <span role="columnheader">{t('live_wall.public.feed.city')}</span>
                <span role="columnheader">{t('live_wall.public.feed.country')}</span>
                <span role="columnheader">{t('live_wall.public.feed.masked_ip')}</span>
                <span role="columnheader">{t('live_wall.public.feed.client_device')}</span>
                <span role="columnheader">{t('live_wall.public.feed.session_duration')}</span>
              </div>
              {activeListenersFeed.slice(0, 6).map((listener, index) => (
                <div className="feed-row" role="row" key={`${listener.maskedIp || index}-${listener.city || 'unknown'}`}>
                  <span role="cell" className="feed-city">
                    {listener.live ? <i aria-label={t('live_wall.public.feed.new_listener')} /> : null}
                    {listener.city}
                  </span>
                  <span role="cell">{listener.country}</span>
                  <span role="cell" className="masked-ip">
                    {listener.maskedIp}
                  </span>
                  <span role="cell">{listener.clientDevice}</span>
                  <strong role="cell">{listener.sessionDuration}</strong>
                </div>
              ))}
            </div>
          </section>

          {showDevicesInBottom ? <DevicesCard devices={devices} t={t} /> : null}
        </section>
      ) : null}
    </main>
  );
}
